import { generateClient } from 'aws-amplify/api';
import * as queries from '../graphql/queries';
import { downloadCsv } from './csv';
import { reactionTypeForNumber } from './reactions';

const client = generateClient();

export async function playerCardsCSV(startDate: Date, endDate: Date) {
  const cards = [];
  let nextToken;
  do {
    const result = (await client.graphql({
      query: queries.listPlayerCards,
      variables: {
        filter: {
          and: [
            { createdAt: { ge: startDate.toISOString() } },
            { createdAt: { le: endDate.toISOString() } }
          ]
        },
        nextToken
      }
    })) as any;
    cards.push(...result.data.listPlayerCards.items);
    nextToken = result.data.listPlayerCards.nextToken;
  } while (nextToken);

  const cardData = cards.map((card) => {
    return {
      id: card.id,
      sessionId: card.sessionId,
      roundId: card.gameId,
      playerId: card.playerId,
      createdAt: card.createdAt,
      link: `${window.location.origin}/dashboard/metrics/card/${card.id}`
    };
  });

  downloadCsv(
    cardData,
    [
      { key: 'id', title: 'Card ID' },
      { key: 'sessionId', title: 'Session ID' },
      { key: 'roundId', title: 'Round ID' },
      { key: 'playerId', title: 'Player ID' },
      { key: 'createdAt', title: 'Created At' },
      { key: 'link', title: 'Link' }
    ],
    'player-cards'
  );
}

export async function playerCSV(startDate: Date, endDate: Date) {
  const players = [];
  let nextToken = null;
  do {
    const result = (await client.graphql({
      query: queries.listPlayers,
      variables: {
        filter: {
          and: [
            { createdAt: { ge: startDate.toISOString() } },
            { createdAt: { le: endDate.toISOString() } }
          ]
        },
        nextToken
      }
    })) as any;
    players.push(...result.data.listPlayers.items);
    nextToken = result.data.listPlayers.nextToken;
  } while (nextToken);

  const playerData = players.map((player) => {
    return {
      id: player.id,
      name: player.name,
      sessionId: player.sessionId,
      feelingBefore: player.feelingBefore,
      feelingAfter: player.feelingAfter,
      question: player.question,
      questionAnswer: player.questionAnswer
        ? player.questionAnswer == 'true'
          ? 'positive'
          : 'negative'
        : null,
      createdAt: player.createdAt
    };
  });

  downloadCsv(
    playerData,
    [
      { key: 'id', title: 'Player ID' },
      { key: 'name', title: 'Name' },
      { key: 'sessionId', title: 'Session ID' },
      { key: 'feelingBefore', title: 'Feeling Before' },
      { key: 'feelingAfter', title: 'Feeling After' },
      { key: 'question', title: 'Question' },
      { key: 'questionAnswer', title: 'Question Answer' },
      { key: 'createdAt', title: 'Created At' }
    ],
    'players'
  );
}

export async function sessionCSV(startDate: Date, endDate: Date) {
  const sessions = [];
  let nextToken = null;
  do {
    const result = (await client.graphql({
      query: queries.listSessions,
      variables: {
        filter: {
          and: [
            { createdAt: { ge: startDate.toISOString() } },
            { createdAt: { le: endDate.toISOString() } }
          ]
        },
        nextToken
      }
    })) as any;
    sessions.push(...result.data.listSessions.items);
    nextToken = result.data.listSessions.nextToken;
  } while (nextToken);

  const sessionData = sessions.map((session) => {
    return {
      id: session.id,
      title: session.title,
      hostId: session.host,
      rounds: session.games?.items.map((game: { id: string }) => game!.id).join(', '),
      createdAt: session.createdAt,
      questionType: session.questionType,
      link: `${window.location.origin}/dashboard/metrics/cards/${session.id}`
    };
  });

  downloadCsv(
    sessionData,
    [
      { key: 'id', title: 'Session ID' },
      { key: 'title', title: 'Title' },
      { key: 'hostId', title: 'Host ID' },
      { key: 'rounds', title: 'Round IDs' },
      { key: 'questionType', title: 'Question Type' },
      { key: 'createdAt', title: 'Created At' },
      { key: 'link', title: 'Link' }
    ],
    'sessions'
  );
}

export async function artworkReactionsCSV(startDate: Date, endDate: Date) {
  const reactions = [];
  let nextToken = null;
  do {
    const result = (await client.graphql({
      query: queries.listArtworkReactions,
      variables: {
        filter: {
          and: [
            { createdAt: { ge: startDate.toISOString() } },
            { createdAt: { le: endDate.toISOString() } }
          ]
        },
        nextToken
      }
    })) as any;
    reactions.push(...result.data.listArtworkReactions.items);
    nextToken = result.data.listArtworkReactions.nextToken;
  } while (nextToken);

  const artworkReactionData = reactions.map((reaction) => {
    return {
      id: reaction.id,
      playerId: reaction.playerId,
      playerCardId: reaction.playerCardId,
      reaction: reactionTypeForNumber(reaction.reaction),
      createdAt: reaction.createdAt
    };
  });

  downloadCsv(
    artworkReactionData,
    [
      { key: 'id', title: 'Reaction ID' },
      { key: 'playerId', title: 'Player ID' },
      { key: 'playerCardId', title: 'Player Card ID' },
      { key: 'reaction', title: 'Reaction' },
      { key: 'createdAt', title: 'Created At' }
    ],
    'artwork-reactions'
  );
}

export async function sessionRoundsCSV(startDate: Date, endDate: Date) {
  const rounds = [];
  let nextToken = null;
  do {
    const result = (await client.graphql({
      query: queries.listGames,
      variables: {
        filter: {
          and: [
            { createdAt: { ge: startDate.toISOString() } },
            { createdAt: { le: endDate.toISOString() } }
          ]
        },
        nextToken
      }
    })) as any;
    rounds.push(...result.data.listGames.items);
    nextToken = result.data.listGames.nextToken;
  } while (nextToken);

  const sessionRoundData = rounds.map((round) => {
    return {
      id: round.id,
      sessionId: round.sessionGamesId,
      roundNumber: round.gameIndex + 1,
      prompt: round.promptText,
      createdAt: round.createdAt
    };
  });

  downloadCsv(
    sessionRoundData,
    [
      { key: 'id', title: 'Round ID' },
      { key: 'sessionId', title: 'Session ID' },
      { key: 'prompt', title: 'Prompt' },
      { key: 'createdAt', title: 'Created At' }
    ],
    'session-rounds'
  );
}
